// STY-445: PostHog on react-submissions — same setup as react-frontend / nextjs (capture-mode model,
// consent-free, NO cookie banner). Uses react-frontend's inline-snippet approach (submissions is a
// React SPA too): the official loader stub defines window.posthog and lazy-loads array.js from the
// first-party /relay, then we init with a real options object.
//
//   - First-party relay: api_host = origin + '/relay' (submissions runs under www.stylemepretty.com).
//   - Per-visitor persistence: 'memory' + disable_persistence for the cookieless modes (EU/GPC),
//     else 'localStorage'. No cookies in either mode.
//   - capture_exceptions: true (autocapture of uncaught errors + unhandled rejections).
//   - Session replay starts manually (disable_session_recording: true) only on RECORDING_PATHS and
//     never in a cookieless mode — see updateRecordingForPath + SessionRecordingManager.
//   - capture_mode is registered as a super-property so it rides EVERY event (incl. $exception).
import { shouldRecordSessionPath } from './sessionRecording'
import { resolveCaptureMode, isCookielessMode } from './captureMode'

// Public PostHog client key (same project as react-frontend / nextjs). Safe to ship — the prod-host
// gate below keeps dev/preview surfaces from feeding the prod project.
const POSTHOG_KEY = 'phc_3tE43WKLuIXtEIdKfkmECrgqeXZmvnrlBSAwlaz7s0V'

// Official PostHog loader stub (identical to react-frontend's snippet). Kept as an injected inline
// <script> (not module JS) so the minified vendor blob doesn't trip CRA's build-time lint. On
// init() it creates the array.js script from {api_host}/static/array.js.
const POSTHOG_STUB = '!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture identify".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);'

function injectStub () {
  if (document.getElementById('submissions-posthog-stub')) return
  const s = document.createElement('script')
  s.id = 'submissions-posthog-stub'
  s.textContent = POSTHOG_STUB
  document.head.appendChild(s)
}

// STY-445 (Codex P2 #85): the public submission link /submissions/submission/<id>/<publicHash> carries
// an access credential (the hash) in the URL. Redact that segment from EVERY captured event so it
// never lands in PostHog event properties ($current_url, $pathname, $referrer, autocapture hrefs, …)
// or session-replay URLs. The public route is also excluded from recording (sessionRecording.js),
// which covers the recorded DOM; this before_send covers the URL that rides on events.
function maskSubmissionHash (value) {
  if (typeof value !== 'string') return value
  return value.replace(/(\/submissions\/submission\/\d+\/)[^/?#]+/g, '$1[redacted]')
}

function maskStringsInObject (obj) {
  if (!obj || typeof obj !== 'object') return
  Object.keys(obj).forEach(function (k) {
    if (typeof obj[k] === 'string') obj[k] = maskSubmissionHash(obj[k])
  })
}

function maskPublicSubmissionHash (event) {
  if (!event) return event
  var props = event.properties
  if (props) {
    maskStringsInObject(props)
    // $initial_current_url / $initial_pathname etc. ride in $set/$set_once (person props), which
    // posthog-js exposes both nested under properties AND top-level on the capture result. Mask all
    // of them — masking only properties would still leak the hash via $set_once on a visitor's first
    // event on the public-hash page (Codex P2 #85).
    maskStringsInObject(props.$set)
    maskStringsInObject(props.$set_once)
    // Autocapture element hrefs can also carry the link.
    if (Array.isArray(props.$elements)) {
      props.$elements.forEach(function (el) {
        if (!el) return
        if (typeof el.attr__href === 'string') el.attr__href = maskSubmissionHash(el.attr__href)
        if (typeof el.href === 'string') el.href = maskSubmissionHash(el.href)
      })
    }
  }
  maskStringsInObject(event.$set)
  maskStringsInObject(event.$set_once)
  return event
}

// STY-445 (Codex P2 #85): exceptions can fire before /relay/static/array.js finishes loading — the
// loader stub only queues init/capture/identify (NOT captureException), and render-phase errors (which
// window.onerror / capture_exceptions can't see) would otherwise be dropped in that window. Buffer
// them and flush once PostHog is ready (flush runs from the `loaded` callback). This window is
// non-trivial for submissions: init waits on ensureGeoResolved() then the async array.js load. Capped
// so it can't grow unbounded if PostHog never loads (e.g. off the prod host / SDK blocked).
var pendingExceptions = []

export function reportException (error, properties) {
  if (typeof window !== 'undefined' && window.posthog && window.posthog.__loaded) {
    try { window.posthog.captureException(error, properties) } catch (e) {}
    return
  }
  if (pendingExceptions.length < 50) pendingExceptions.push({ error: error, properties: properties })
}

function flushPendingExceptions () {
  if (typeof window === 'undefined' || !window.posthog || !window.posthog.__loaded) return
  while (pendingExceptions.length) {
    var e = pendingExceptions.shift()
    try { window.posthog.captureException(e.error, e.properties) } catch (err) {}
  }
}

// Init PostHog. prod-canonical-host only (the key is the prod project's; non-prod must not feed it).
// Call AFTER ensureGeoResolved() so the capture mode / persistence reflect the real geo.
export function initPostHog () {
  if (typeof window === 'undefined') return
  const posthogAllowed = process.env.NODE_ENV === 'production' &&
    window.location.hostname === 'www.stylemepretty.com'
  if (!posthogAllowed || (window.posthog && window.posthog.__loaded)) return

  const mode = resolveCaptureMode({})
  const cookieless = isCookielessMode(mode)

  injectStub()
  window.posthog.init(POSTHOG_KEY, {
    api_host: window.location.origin + '/relay',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    // SPA pageviews: react-submissions is a client-routed BrowserRouter app, so capture $pageview on
    // history changes — otherwise only the initial URL of a session is tracked and in-app route
    // changes (dashboard -> submission detail -> post-builder) are missed. (Codex P2 #85.)
    capture_pageview: 'history_change',
    persistence: cookieless ? 'memory' : 'localStorage',
    disable_persistence: cookieless,
    capture_exceptions: true,
    disable_session_recording: true,
    before_send: maskPublicSubmissionHash,
    loaded: function (ph) {
      // localStorage mode: clear any stale opt-out BEFORE register so the super-property isn't
      // dropped. Cookieless disables persistence entirely (no persistent opt-out to clear, and
      // clearing would touch localStorage and break storage-less).
      if (!cookieless) ph.clear_opt_in_out_capturing()
      ph.register({ capture_mode: mode })
      flushPendingExceptions()
      if (!cookieless && shouldRecordSessionPath(window.location.pathname)) ph.startSessionRecording()
    }
  })
}

// Start replay on a recording path, and STOP it when navigating away (SPA nav) — otherwise a session
// that enters /submissions/submission/* would keep recording across the rest of the app, defeating
// the path gate + budget (Codex P2 #85: startSessionRecording records until stopSessionRecording).
// No-ops until PostHog has loaded; never records in a cookieless mode. The initial path is covered by
// the `loaded` callback above. Both PostHog calls are idempotent, so calling on every nav is safe.
export function updateRecordingForPath (pathname) {
  if (typeof window === 'undefined' || !window.posthog || !window.posthog.__loaded) return
  if (isCookielessMode(resolveCaptureMode({}))) return
  if (shouldRecordSessionPath(pathname)) {
    window.posthog.startSessionRecording()
  } else {
    window.posthog.stopSessionRecording()
  }
}
